import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, List, Tooltip } from 'antd';
import axios from 'axios';
import { FileTextOutlined, ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import FeatherSpinner from './components/FeatherSpinner';
import AppPageHeader from './components/AppPageHeader';
import { getAuthHeaders } from './utils/authHeaders';
import { resolveFrontendApiBase } from './utils/apiBase';
import { listM2MHandoffs, listM2MLibraryStatus, m2mErrorMessage, requireM2MIdentity } from './marketing/m2mApi';
import { getM2MBlockingHandoff, isM2MSupportedLanguage } from './marketing/m2mLanguages';
import type { M2MHandoffRecord } from './marketing/types';
import { useM2MIdentity } from './marketing/useM2MIdentity';
import { getBookTargetLanguages, resolveLibraryTranslationStatus, LibraryLanguageStatusBook } from './utils/manageBooksLanguageStatus';
import { canonicalizeLanguageKey, getLanguageCountryCode } from './utils/languageIdentity';
import { getTargetLanguageFromPair } from './utils/languagePairs';
import './MarketingLandingPage.css';

interface Book extends LibraryLanguageStatusBook { bookguid: string; title: string; author?: string; coverUrl?: string; series?: string; seriesBookNumber?: string | number }
interface Props { userId: string }
const needsProgressRefresh = (book: Book) => book.progress_state === 'refreshing' ||
  book.progress_state === 'unavailable' || book.flagStatus === 'loading' || book.flagStatus === 'error' ||
  getBookTargetLanguages(book).some(language => resolveLibraryTranslationStatus(book, language, []) === 'unavailable');
type ReportState = { items: M2MHandoffRecord[]; hasMore?: boolean };
const orderStatus = (status?: string) => status === 'created' || status === 'awaiting_payment'
  ? 'Awaiting payment · View order' : status === 'paid' ? 'Payment received · View order' : 'Report processing · View order';

export const marketingBookUrl = (bookId: string, edition?: string) =>
  `/book-detail/${encodeURIComponent(bookId)}?${new URLSearchParams({ tab: 'marketing', marketingView: 'reports', ...(edition ? { edition } : {}) })}`;

const MarketingBook: React.FC<{ book: Book; userId: string; cached?: ReportState; onLoaded: (id: string, state: ReportState) => void }> = ({ book, userId, cached, onLoaded }) => {
  const navigate = useNavigate();
  const [failedCover, setFailedCover] = useState<string>();
  const [reports, setReports] = useState<M2MHandoffRecord[] | null>(null);
  const [reportError, setReportError] = useState(false);
  const [reportAttempt, setReportAttempt] = useState(0);
  useEffect(() => {
    if (cached && reportAttempt === 0) {
      setReports(cached.items);
      setReportError(Boolean(cached.hasMore));
      return;
    }
    const controller = new AbortController();
    setReports(null);
    setReportError(false);
    listM2MHandoffs({ userId, bookId: book.bookguid }, controller.signal).then(result => {
      if (!controller.signal.aborted) {
        onLoaded(book.bookguid, result);
        setReportAttempt(0);
        setReports(result.items);
        setReportError(Boolean(result.hasMore));
      }
    }).catch(() => { if (!controller.signal.aborted) setReportError(true); });
    return () => controller.abort();
  }, [userId, book.bookguid, reportAttempt, cached, onLoaded]);
  const languages = getBookTargetLanguages(book).filter(language =>
    isM2MSupportedLanguage(language) && (resolveLibraryTranslationStatus(book, language, []) === 'complete' ||
    Boolean(reports && getM2MBlockingHandoff(reports, language))));
  const readyCount = reports?.filter(item => item.status === 'completed').length || 0;
  const activeCount = reports?.filter(item => ['created', 'awaiting_payment', 'paid', 'processing'].includes(item.status || '')).length || 0;
  const summary = [readyCount ? `${readyCount} report${readyCount === 1 ? '' : 's'} ready` : '',
    activeCount ? `${activeCount} active order${activeCount === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
  return <List.Item className="marketing-landing-row">
    <div className="marketing-landing-identity">
      <div className="marketing-landing-cover">
        {book.coverUrl && failedCover !== book.coverUrl
          ? <img src={book.coverUrl} alt={`${book.title} cover`} loading="lazy" onError={() => setFailedCover(book.coverUrl)} />
          : <span aria-label="No cover available">{book.title.slice(0, 1).toUpperCase()}</span>}
      </div>
      <div className="marketing-landing-book">
        <strong>{book.title}</strong><span>{book.author || 'Unknown author'}</span>
        {book.series && <span className="marketing-landing-series">{book.series}{book.seriesBookNumber != null && book.seriesBookNumber !== '' ? ` · Book ${book.seriesBookNumber}` : ''}</span>}
      </div>
    </div>
    <div className="marketing-landing-report-details">
      {reports !== null && !reportError && <span className="marketing-landing-status">{summary || 'No reports yet'}</span>}
      {languages.length > 0 ? <>
        <div className="marketing-landing-languages" role="group" aria-label={`Marketing languages for ${book.title}`}>
          {languages.map(language => {
            const pair = book.translation_pairs?.find(value => typeof value === 'string' &&
              canonicalizeLanguageKey(getTargetLanguageFromPair(value)) === canonicalizeLanguageKey(language));
            const handoff = reports && getM2MBlockingHandoff(reports, language);
            const action = reportError || reports === null ? 'Open Marketing'
              : handoff?.status === 'completed' ? 'View report' : handoff ? 'View order'
                : isM2MSupportedLanguage(language) ? 'Explore' : 'Open Marketing';
            const complete = resolveLibraryTranslationStatus(book, language, []) === 'complete';
            const country = getLanguageCountryCode(language);
            const status = reportError ? 'Report status unavailable' : reports === null ? 'Checking report status…'
              : handoff?.status === 'completed' ? 'Report ready · View report' : handoff ? orderStatus(handoff.status)
                : isM2MSupportedLanguage(language) ? 'No report yet · Explore Deep Dive — $50 per language' : 'Open Marketing';
            return <Tooltip key={language} trigger={['hover', 'focus']} mouseEnterDelay={0.15} title={<>
              <strong>{language}</strong><div>{complete ? 'Translation complete' : 'Translation status unconfirmed'}</div>
              <div>{status}</div>
            </>}>
              <button type="button" className="marketing-landing-flag"
                aria-label={`${language}: ${action} for ${book.title}`}
                onClick={() => navigate(marketingBookUrl(book.bookguid, pair as string))}>
                <span className={`marketing-landing-flag-image${complete ? ' is-complete' : ''}`} aria-hidden="true"
                  style={country && country !== 'HE' ? { backgroundImage: `url(https://flagcdn.com/w40/${country.toLowerCase()}.png)` } : undefined}>
                  {country === 'HE' ? 'א' : !country ? language.slice(0, 2).toUpperCase() : null}
                </span>
                <span className="marketing-landing-flag-status" aria-hidden="true">
                  {action === 'View report' ? <FileTextOutlined /> : action === 'View order' ? <ClockCircleOutlined />
                    : action === 'Explore' ? <PlusOutlined /> : null}
                </span>
              </button>
            </Tooltip>;
          })}
        </div>
        {reports === null && !reportError && <span className="marketing-landing-status">Checking report status…</span>}
        {reportError && <button type="button" className="marketing-landing-retry" onClick={() => setReportAttempt(value => value + 1)}>Retry report status</button>}
      </> : <>
        <span className="marketing-landing-status">{book.progress_state === 'refreshing' || book.progress_state === 'unavailable' || book.flagStatus === 'loading' || book.flagStatus === 'error'
          ? (book.flagStatus === 'loading' ? 'Checking translation status…' : book.progress_state === 'refreshing' ? 'Translation status refresh pending' : 'Translation status unavailable') : 'No completed translations in supported Deep Dive languages yet'}</span>
        <Button onClick={() => navigate(marketingBookUrl(book.bookguid))}
          aria-label={`Open Marketing for ${book.title}`}>Open Marketing</Button>
      </>}
    </div>
  </List.Item>;
};

const MarketingBookPicker: React.FC<Props> = ({ userId }) => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [checkingTranslations, setCheckingTranslations] = useState(false);
  const [pendingTranslationCount, setPendingTranslationCount] = useState(0);
  const indexLoaded = useRef(false);
  const booksRef = useRef(books);
  booksRef.current = books;
  const [translationError, setTranslationError] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [otherBooksOpen, setOtherBooksOpen] = useState<boolean | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [filter, setFilter] = useState('all');
  const [reportCache, setReportCache] = useState<Record<string, ReportState>>({});
  const [checkingFilters, setCheckingFilters] = useState(false);
  const [filterError, setFilterError] = useState(false);
  const [libraryStatusLoaded, setLibraryStatusLoaded] = useState(false);
  const [filterAttempt, setFilterAttempt] = useState(0);
  const rememberReports = useCallback((id: string, state: ReportState) => {
    setReportCache(previous => ({ ...previous, [id]: state }));
  }, []);
  useEffect(() => {
    if (filter === 'all' || !books.length || libraryStatusLoaded) return;
    const controller = new AbortController();
    setCheckingFilters(true);
    setFilterError(false);
    listM2MLibraryStatus(userId, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setReportCache(Object.fromEntries(books.map(book => [book.bookguid, { items: result.books[book.bookguid] || [] }])));
      setCheckingFilters(false);
      setLibraryStatusLoaded(true);
    }).catch(() => {
      if (!controller.signal.aborted) setFilterError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setCheckingFilters(false);
    });
    return () => controller.abort();
  }, [filter, books, userId, filterAttempt, libraryStatusLoaded]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(!indexLoaded.current);
    setError(undefined);
    setTranslationError(false);
    setPendingTranslationCount(0);
    const load = async () => {
      try {
        requireM2MIdentity(userId);
        const headers = await getAuthHeaders();
        requireM2MIdentity(userId);
        const response = !indexLoaded.current
          ? await axios.get<Book[]>(`${resolveFrontendApiBase()}/uploadedbooks/user/${encodeURIComponent(userId)}/index`, { headers, signal: controller.signal })
          : { data: booksRef.current };
        requireM2MIdentity(userId);
        if (controller.signal.aborted) return;
        indexLoaded.current = true;
        setBooks(response.data);
        setLoading(false);
        const pending = response.data.filter(needsProgressRefresh).map(book => book.bookguid);
        setCheckingTranslations(pending.length > 0);
        // Finish a small group before scheduling more backend reconstruction work.
        // The backend has four refresh workers; a catalog-wide queue outlives short polling budgets.
        for (let offset = 0; offset < pending.length && !controller.signal.aborted; offset += 10) {
          let group = pending.slice(offset, offset + 10);
          for (let pass = 0; pass < 5 && group.length && !controller.signal.aborted; pass++) {
            const ids = group;
            group = [];
            setBooks(previous => previous.map(book => ids.includes(book.bookguid) ? { ...book, flagStatus: 'loading' } : book));
            try {
              requireM2MIdentity(userId);
              const detailHeaders = await getAuthHeaders();
              requireM2MIdentity(userId);
              const details = await axios.post<Book[]>(`${resolveFrontendApiBase()}/uploadedbooks/details`,
                { user_id: userId, book_ids: ids }, { headers: detailHeaders, signal: controller.signal });
              requireM2MIdentity(userId);
              if (controller.signal.aborted) return;
              const byId = new Map(details.data.map(book => [book.bookguid, book]));
              if (ids.some(id => !byId.has(id))) setTranslationError(true);
              ids.forEach(id => { const detail = byId.get(id); if (detail && needsProgressRefresh(detail)) group.push(id); });
              setBooks(previous => previous.map(book => {
                if (!ids.includes(book.bookguid)) return book;
                const detail = byId.get(book.bookguid);
                return detail ? { ...book, ...detail, flagStatus: needsProgressRefresh(detail) ? 'loading' : 'ready' }
                  : { ...book, progress_state: 'unavailable', flagStatus: 'error' };
              }));
            } catch {
              if (controller.signal.aborted) return;
              setTranslationError(true);
              setBooks(previous => previous.map(book => ids.includes(book.bookguid)
                ? { ...book, progress_state: 'unavailable', flagStatus: 'error' } : book));
            }
            if (group.length && pass < 4 && !controller.signal.aborted) {
              await new Promise<void>(resolve => {
                const finish = () => { clearTimeout(timer); controller.signal.removeEventListener('abort', finish); resolve(); };
                const timer = setTimeout(finish, Math.min(4000 * (pass + 1), 12000));
                controller.signal.addEventListener('abort', finish, { once: true });
              });
            }
          }
          if (!controller.signal.aborted && group.length) {
            setPendingTranslationCount(count => count + group.length);
            setBooks(previous => previous.map(book => group.includes(book.bookguid)
              ? { ...book, progress_state: 'refreshing', flagStatus: 'ready' } : book));
          }
        }
        if (!controller.signal.aborted) setCheckingTranslations(false);
      } catch (reason) {
        if (!controller.signal.aborted) setError(m2mErrorMessage(reason, 'Unable to load your books. Please try again.'));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => controller.abort();
  }, [userId, attempt]);

  const matches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return books.filter(book => `${book.title} ${book.author || ''}`.toLocaleLowerCase().includes(query));
  }, [books, search]);

  const readyBooks = matches.filter(book => getBookTargetLanguages(book).some(language =>
    isM2MSupportedLanguage(language) && resolveLibraryTranslationStatus(book, language, []) === 'complete'));
  const otherBooks = matches.filter(book => !readyBooks.includes(book));
  const filteredBooks = filter === 'all' ? readyBooks : matches.filter(book =>
    reportCache[book.bookguid]?.items.some(item => filter === 'ready' ? item.status === 'completed'
      : ['created', 'awaiting_payment', 'paid', 'processing'].includes(item.status || '')));
  const showOtherBooks = otherBooksOpen ?? readyBooks.length === 0;

  return <main className="marketing-landing">
    <AppPageHeader title="Deep Dive" titleId="marketing-landing-title"
      description="Choose a translated edition to explore Deep Dive reports or view your existing reports." />
    {loading ? <div className="marketing-landing-loading"><FeatherSpinner style={{ width: 64, height: 64 }} alt="Loading your books…" /><span>Loading your books…</span></div>
      : error ? <Alert type="error" message={error} action={<Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>} />
        : books.length === 0 ? <Empty description="Your library is empty."><Link to="/manage-books">Upload a book in Manage Books</Link></Empty>
          : <section aria-label="Choose a book">
            <Input.Search aria-label="Search books by title or author" placeholder="Search by title or author" value={search}
              onChange={event => { setSearch(event.target.value); setPage(1); }} allowClear />
            {checkingTranslations && <p role="status">Checking translation status for books with older cached progress…</p>}
            {pendingTranslationCount > 0 && !checkingTranslations && <Alert type="info"
              message={`${pendingTranslationCount} book${pendingTranslationCount === 1 ? '' : 's'} still awaiting refreshed translation status. Verified editions are ready to use.`}
              action={<Button onClick={() => setAttempt(value => value + 1)}>Check pending translations</Button>} />}
            {translationError && !checkingTranslations && <Alert type="warning" message="Some translation statuses are unavailable. This does not mean those translations are incomplete."
              action={<Button onClick={() => setAttempt(value => value + 1)}>Retry translation status</Button>} />}
            <div className="marketing-landing-filters" role="group" aria-label="Filter books by report status">
              {[['all', 'All translated books'], ['ready', 'Reports ready'], ['active', 'Active orders']].map(([value, label]) =>
                <button key={value} type="button" aria-pressed={filter === value}
                  onClick={() => { setFilter(value); setPage(1); }}>{label}</button>)}
            </div>
            {filter !== 'all' && checkingFilters && <div className="marketing-landing-loading"><FeatherSpinner style={{ width: 64, height: 64 }} alt="Checking reports across your library…" /><span>Checking reports across your library…</span></div>}
            {filter !== 'all' && filterError && <Alert type="warning" message="Some report statuses could not be checked. Results may be incomplete."
              action={<Button onClick={() => setFilterAttempt(value => value + 1)}>Retry status check</Button>} />}
            {readyBooks.length > 0 && <div className="marketing-landing-legend" aria-label="Report status legend">
              <span><span className="marketing-landing-complete-key" /> Green outline: translation complete</span>
              <span><FileTextOutlined /> Report ready</span><span><ClockCircleOutlined /> Active order</span><span><PlusOutlined /> Explore Deep Dive</span>
            </div>}
            {matches.length === 0 ? <Empty description="No books match your search." /> : <>
              {filter !== 'all' && !checkingFilters && !filterError && filteredBooks.length === 0 && <Empty description={filter === 'ready' ? 'No ready reports match your search.' : 'No active orders match your search.'} />}
              {filteredBooks.length > 0 && <List className="marketing-landing-grid" dataSource={filteredBooks}
                pagination={{ current: page, onChange: setPage, pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
                renderItem={book => <MarketingBook key={book.bookguid} book={book} userId={userId} cached={reportCache[book.bookguid]} onLoaded={rememberReports} />} />}
              {filter === 'all' && otherBooks.length > 0 && <section className="marketing-landing-other">
                <button type="button" className="marketing-landing-other-toggle" aria-expanded={showOtherBooks}
                  aria-controls="marketing-other-books" onClick={() => setOtherBooksOpen(!showOtherBooks)}>
                  <span aria-hidden="true">{showOtherBooks ? '▾' : '▸'}</span> Other books <span>({otherBooks.length})</span>
                </button>
                {showOtherBooks && <div id="marketing-other-books">
                  <p>These books have no verified completed editions in supported Deep Dive languages. Eligible editions move above once their status is verified.</p>
                  <List key={search} className="marketing-landing-grid" dataSource={otherBooks}
                    pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
                    renderItem={book => <MarketingBook key={book.bookguid} book={book} userId={userId} cached={reportCache[book.bookguid]} onLoaded={rememberReports} />} />
                </div>}
              </section>}
            </>}
          </section>}
  </main>;
};

const MarketingLandingPage: React.FC<Props> = props => {
  const authorized = useM2MIdentity(props.userId);
  return authorized ? <MarketingBookPicker key={props.userId} {...props} /> : <Alert type="info" message="Sign in to access Marketing." />;
};

export default MarketingLandingPage;
